import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components';
import { leadsApi } from '../utils/api';
import type { User, LeadListItem } from '../types';
import { formatDate } from '../utils/date';
import { PhoneNumber } from '../components/PhoneNumber';

export function Dashboard() {
  const [user, setUser] = useState<User | null>(null);
  const [leads, setLeads] = useState<LeadListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('today');
  const [search, setSearch] = useState('');
  const [campaignFilter, setCampaignFilter] = useState('');
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    const stored = localStorage.getItem('rewagent_user');
    if (stored) setUser(JSON.parse(stored));
    else navigate('/');
  }, [navigate]);

  useEffect(() => {
    const fetchLeads = async () => {
      if (!user) return;
      setLoading(true);
      setError(null);
      try {
        const now = new Date();
        let dateFrom: string | undefined;
        let dateTo: string | undefined;

        if (dateFilter === 'today') {
          dateFrom = now.toISOString().split('T')[0];
        } else if (dateFilter === 'yesterday') {
          const yesterday = new Date(now);
          yesterday.setDate(yesterday.getDate() - 1);
          dateFrom = yesterday.toISOString().split('T')[0];
          dateTo = dateFrom;
        } else if (dateFilter === 'week') {
          const weekAgo = new Date(now);
          weekAgo.setDate(weekAgo.getDate() - 7);
          dateFrom = weekAgo.toISOString().split('T')[0];
        } else if (dateFilter === 'month') {
          const monthAgo = new Date(now);
          monthAgo.setDate(monthAgo.getDate() - 30);
          dateFrom = monthAgo.toISOString().split('T')[0];
        }

        const data = await leadsApi.list({
          status: statusFilter || undefined,
          date_from: dateFrom,
          date_to: dateTo,
          search: search || undefined,
          campaign: campaignFilter || undefined,
          page,
          limit: 20,
        });
        setLeads(data.leads || []);
        setTotal(data.pagination?.total || 0);
        setTotalPages(data.pagination?.totalPages || 1);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load leads');
      } finally {
        setLoading(false);
      }
    };
    fetchLeads();
  }, [user, statusFilter, dateFilter, search, campaignFilter, page]);

  const handleLogout = async () => {
    try { await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }); } catch { }
    localStorage.removeItem('rewagent_user');
    localStorage.removeItem('rewagent_token');
    navigate('/');
  };

  const handleReset = () => {
    setStatusFilter('');
    setDateFilter('today');
    setSearch('');
    setCampaignFilter('');
    setPage(1);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'qualified': return 'bg-green-100 text-green-800 border-green-200';
      case 'disqualified': return 'bg-red-100 text-red-800 border-red-200';
      case 'new': return 'bg-[#f3ede3] text-[#1a1a1a] border-[#e3dbcf]';
      default: return 'bg-[#fcfbfa] text-[#6b635a] border-[#e3dbcf]';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 7) return 'text-[#1a1a1a] font-bold';
    if (score >= 4) return 'text-[#6b635a] font-semibold';
    return 'text-[#8c8578]';
  };

  const inputCls = 'w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-2.5 focus:outline-none focus:border-[#1a1a1a] transition-all text-sm';

  if (!user) return null;

  return (
    <Layout showHeader userName={user.name} onLogout={handleLogout}>
      <div className="min-h-screen bg-[#f3ede3] font-[Inter]">
        <div className="max-w-[100rem] mx-auto px-6 lg:px-8 py-10">
          {/* Page Header */}
          <div className="mb-10 flex items-end justify-between">
            <div>
              <h1 className="text-4xl font-bold text-[#1a1a1a] mb-2 tracking-tight">Leads</h1>
              <p className="text-[#6b635a] text-sm uppercase tracking-widest font-semibold">{user.client_name}</p>
            </div>
            {user.role !== 'qa' && (
              <div className="text-right">
                <p className="text-xs font-semibold text-[#8c8578] uppercase tracking-widest mb-1">Balance</p>
                <p className={`text-2xl font-bold tracking-tight ${(user.balance || 0) < 0 ? 'text-red-600' : 'text-[#1a1a1a]'}`}>
                  ${Number(user.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            )}
          </div>

          {/* Filters */}
          <div className="bg-[#fcfbfa] border border-[#e3dbcf] rounded-2xl p-6 mb-8 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-5">
              <div>
                <label className="text-xs font-semibold text-[#8c8578] uppercase tracking-widest mb-3 block">Status</label>
                <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className={inputCls}>
                  <option value="">All Statuses</option>
                  <option value="new">New</option>
                  <option value="qualified">Qualified</option>
                  <option value="disqualified">Disqualified</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#8c8578] uppercase tracking-widest mb-3 block">Date Range</label>
                <select value={dateFilter} onChange={(e) => { setDateFilter(e.target.value); setPage(1); }} className={inputCls}>
                  <option value="today">Today (EST)</option>
                  <option value="yesterday">Yesterday</option>
                  <option value="week">Last 7 Days</option>
                  <option value="month">Last 30 Days</option>
                  <option value="all">All Time</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#8c8578] uppercase tracking-widest mb-3 block">Search</label>
                <input type="text" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  placeholder="Name, address..." className={inputCls} />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#8c8578] uppercase tracking-widest mb-3 block">Campaign</label>
                <input type="text" value={campaignFilter} onChange={(e) => { setCampaignFilter(e.target.value); setPage(1); }}
                  placeholder="Campaign name..." className={inputCls} />
              </div>
              <div className="flex items-end">
                <button onClick={handleReset} className="w-full px-4 py-2.5 bg-[#f3ede3] hover:bg-[#1a1a1a] text-[#1a1a1a] hover:text-[#f3ede3] border border-[#e3dbcf] rounded-lg text-sm font-medium transition-all">
                  Reset
                </button>
              </div>
            </div>
          </div>

          <div className="mb-5 text-sm font-medium text-[#8c8578] uppercase tracking-widest">
            {total === 0 ? '0 LEADS' : `${leads.length} OF ${total} LEADS`}
          </div>

          {error && <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">{error}</div>}

          {loading && (
            <div className="flex items-center justify-center py-24">
              <svg className="animate-spin h-8 w-8 text-[#1a1a1a]" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            </div>
          )}

          {!loading && leads.length > 0 && (
            <div className="bg-[#fcfbfa] border border-[#e3dbcf] rounded-2xl overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#e3dbcf]">
                      <th className="px-6 py-5 text-left text-xs font-semibold text-[#8c8578] uppercase tracking-widest">Owner</th>
                      <th className="px-6 py-5 text-left text-xs font-semibold text-[#8c8578] uppercase tracking-widest">Phone</th>
                      <th className="px-6 py-5 text-left text-xs font-semibold text-[#8c8578] uppercase tracking-widest">Address</th>
                      <th className="px-6 py-5 text-left text-xs font-semibold text-[#8c8578] uppercase tracking-widest">Type</th>
                      <th className="px-6 py-5 text-left text-xs font-semibold text-[#8c8578] uppercase tracking-widest">Campaign</th>
                      <th className="px-6 py-5 text-left text-xs font-semibold text-[#8c8578] uppercase tracking-widest">Status</th>
                      <th className="px-6 py-5 text-left text-xs font-semibold text-[#8c8578] uppercase tracking-widest">Score</th>
                      {user.role !== 'qa' && <th className="px-6 py-5 text-left text-xs font-semibold text-[#8c8578] uppercase tracking-widest">Cost</th>}
                      <th className="px-6 py-5 text-left text-xs font-semibold text-[#8c8578] uppercase tracking-widest">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#e3dbcf]">
                    {leads.map((lead) => (
                      <tr key={lead.id} className="hover:bg-[#f3ede3]/50 cursor-pointer transition-colors" onClick={() => navigate(`/lead/${lead.id}`)}>
                        <td className="px-6 py-5"><span className="text-[#1a1a1a] font-semibold">{`${lead.owner_firstname} ${lead.owner_lastname}`}</span></td>
                        <td className="px-6 py-5 text-[#6b635a] text-sm">
                          <PhoneNumber phone={lead.owner_phone} status={lead.status} />
                        </td>
                        <td className="px-6 py-5 text-[#6b635a] text-sm max-w-sm truncate">{lead.prop_address}</td>
                        <td className="px-6 py-5 text-[#6b635a] text-sm">{lead.prop_type}</td>
                        <td className="px-6 py-5 text-[#6b635a] text-sm">{lead.campaign_name || 'N/A'}</td>
                        <td className="px-6 py-5">
                          <span className={`inline-flex px-3 py-1.5 rounded text-xs font-semibold tracking-wide border ${getStatusBadge(lead.status)}`}>
                            {lead.status.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-5">
                          <span className={`text-sm ${getScoreColor(lead.lead_score)}`}>{lead.lead_score}</span>
                          <span className="text-xs text-[#b8b0a5] ml-0.5">/10</span>
                        </td>
                        {user.role !== 'qa' && (
                          <td className="px-6 py-5">
                            <span className="text-sm font-bold text-[#1a1a1a]">${Number(lead.lead_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </td>
                        )}
                        <td className="px-6 py-5 text-[#8c8578] text-sm font-medium tracking-wide">{formatDate(lead.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!loading && leads.length === 0 && (
            <div className="text-center py-24 bg-[#fcfbfa] border border-[#e3dbcf] rounded-2xl">
              <svg className="w-14 h-14 text-[#b8b0a5] mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <h3 className="text-base font-semibold text-[#1a1a1a] mb-1">No leads found</h3>
              <p className="text-sm text-[#8c8578]">Try adjusting your filters</p>
            </div>
          )}

          {!loading && totalPages > 1 && (
            <div className="mt-8 flex items-center justify-center gap-4">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-5 py-2.5 bg-[#fcfbfa] hover:bg-[#1a1a1a] text-[#1a1a1a] hover:text-[#fcfbfa] border border-[#e3dbcf] rounded-lg text-sm font-medium disabled:opacity-40 transition-all">Previous</button>
              <span className="text-[#8c8578] text-sm font-medium uppercase tracking-widest">Page {page} of {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-5 py-2.5 bg-[#fcfbfa] hover:bg-[#1a1a1a] text-[#1a1a1a] hover:text-[#fcfbfa] border border-[#e3dbcf] rounded-lg text-sm font-medium disabled:opacity-40 transition-all">Next</button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
